import jsPDF from "jspdf";
import type { User } from "firebase/auth";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  type Unsubscribe
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import type { CompanyRecord } from "./companies";
import { applyCommercialAdjustment, calculateServiceEstimate } from "./commercial";
import type { ContractTemplate, GeneratedContract, GeneratedContractStatus, ServiceCatalogItem } from "../types/imobi";
import { db, storage } from "./firebase";

function formatCurrencyBR(value: number): string {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

function formatDateBR(value: string | undefined): string {
  if (!value) {
    return "-";
  }
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("pt-BR");
}

function computeCompanyPlanPricing(company: CompanyRecord, serviceCatalog: ServiceCatalogItem[]): { baseValue?: number; finalValue?: number } {
  const plan = serviceCatalog.find((item) => item.id === company.settings?.activePlanId);
  if (!plan) {
    return {};
  }
  const baseValue = calculateServiceEstimate(plan, 1);
  const finalValue = applyCommercialAdjustment(baseValue, company.settings?.commercialAdjustmentType, company.settings?.commercialAdjustmentValue);
  return { baseValue, finalValue };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Oldest format: one big plain-text string, blank line = new paragraph. */
function migrateLegacyBodyStringToHtml(body: string): string {
  return body
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`)
    .join("");
}

/** Short-lived intermediate format: an array of {type, text, bold, color} blocks. */
function migrateLegacyBlocksToHtml(blocks: unknown[]): string {
  type LegacyBlock = { type?: string; text?: string; bold?: boolean; color?: string };
  const html: string[] = [];
  let openList = false;

  (blocks as LegacyBlock[]).forEach((block) => {
    const isBullet = block.type === "bullet";
    if (isBullet && !openList) {
      html.push("<ul>");
      openList = true;
    }
    if (!isBullet && openList) {
      html.push("</ul>");
      openList = false;
    }

    const text = escapeHtml(String(block.text ?? ""));
    const inner = block.color ? `<span style="color: ${escapeHtml(String(block.color))}">${text}</span>` : text;
    const withBold = block.bold ? `<b>${inner}</b>` : inner;
    html.push(isBullet ? `<li>${withBold}</li>` : `<p>${withBold}</p>`);
  });

  if (openList) {
    html.push("</ul>");
  }

  return html.join("");
}

const ALLOWED_CONTRACT_TAGS = new Set(["P", "DIV", "B", "STRONG", "U", "SPAN", "FONT", "UL", "OL", "LI", "BR"]);

/** Keeps only the tags/attributes the editor and the PDF renderer both understand. */
export function sanitizeContractHtml(html: string): string {
  if (typeof window === "undefined" || !html.trim()) {
    return html;
  }

  const parsed = new DOMParser().parseFromString(html, "text/html");

  function clean(node: ChildNode) {
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }
    const element = node as HTMLElement;
    if (!ALLOWED_CONTRACT_TAGS.has(element.tagName)) {
      element.replaceWith(document.createTextNode(element.textContent ?? ""));
      return;
    }

    const color = element.style?.color;
    const fontWeight = element.style?.fontWeight;
    const fontSize = element.style?.fontSize;
    const textDecoration = element.style?.textDecoration;
    const fontColorAttr = element.tagName === "FONT" ? element.getAttribute("color") : null;
    Array.from(element.attributes).forEach((attribute) => element.removeAttribute(attribute.name));
    if (color) {
      element.style.color = color;
    }
    if (fontWeight) {
      element.style.fontWeight = fontWeight;
    }
    if (fontSize) {
      element.style.fontSize = fontSize;
    }
    if (textDecoration && textDecoration.includes("underline")) {
      element.style.textDecoration = "underline";
    }
    if (fontColorAttr) {
      element.setAttribute("color", fontColorAttr);
    }

    Array.from(element.childNodes).forEach(clean);
  }

  Array.from(parsed.body.childNodes).forEach(clean);
  return parsed.body.innerHTML;
}

const IMAGE_LOAD_TIMEOUT_MS = 6000;

type PdfImage = {
  dataUrl: string;
  format: "PNG" | "JPEG";
};

function dataUrlFormat(dataUrl: string): "PNG" | "JPEG" | null {
  if (dataUrl.startsWith("data:image/png")) {
    return "PNG";
  }

  if (dataUrl.startsWith("data:image/jpeg") || dataUrl.startsWith("data:image/jpg")) {
    return "JPEG";
  }

  return null;
}

function withTimeout<T>(promise: Promise<T>, timeoutMs = IMAGE_LOAD_TIMEOUT_MS): Promise<T | null> {
  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => resolve(null), timeoutMs);
    promise
      .then((result) => resolve(result))
      .catch(() => resolve(null))
      .finally(() => window.clearTimeout(timeout));
  });
}

function readBlobAsDataUrl(blob: Blob): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(typeof reader.result === "string" ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

async function normalizeImageDataUrl(dataUrl: string): Promise<PdfImage | null> {
  const directFormat = dataUrlFormat(dataUrl);
  if (directFormat) {
    return { dataUrl, format: directFormat };
  }

  return new Promise((resolve) => {
    const image = new window.Image();
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;
        const context = canvas.getContext("2d");
        if (!context || !canvas.width || !canvas.height) {
          resolve(null);
          return;
        }

        context.drawImage(image, 0, 0);
        resolve({ dataUrl: canvas.toDataURL("image/png"), format: "PNG" });
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = dataUrl;
  });
}

async function loadImageFromUrlCanvas(url: string): Promise<PdfImage | null> {
  return new Promise((resolve) => {
    const image = new window.Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;
        const context = canvas.getContext("2d");
        if (!context || !canvas.width || !canvas.height) {
          resolve(null);
          return;
        }

        context.drawImage(image, 0, 0);
        resolve({ dataUrl: canvas.toDataURL("image/png"), format: "PNG" });
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

async function loadPdfImage(url: string | undefined): Promise<PdfImage | null> {
  if (!url) {
    return null;
  }

  try {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), IMAGE_LOAD_TIMEOUT_MS);
    const response = await fetch(url, { mode: "cors", cache: "force-cache", signal: controller.signal });
    window.clearTimeout(timeout);
    if (!response.ok) {
      return withTimeout(loadImageFromUrlCanvas(url));
    }

    const dataUrl = await readBlobAsDataUrl(await response.blob());
    if (dataUrl) {
      const normalized = await normalizeImageDataUrl(dataUrl);
      if (normalized) {
        return normalized;
      }
    }

    return withTimeout(loadImageFromUrlCanvas(url));
  } catch {
    return withTimeout(loadImageFromUrlCanvas(url));
  }
}

function mapContractTemplate(id: string, data: Record<string, unknown>): ContractTemplate {
  let bodyHtml: string;
  if (typeof data.bodyHtml === "string" && data.bodyHtml.trim()) {
    bodyHtml = data.bodyHtml;
  } else if (Array.isArray(data.bodyBlocks)) {
    bodyHtml = migrateLegacyBlocksToHtml(data.bodyBlocks);
  } else {
    bodyHtml = migrateLegacyBodyStringToHtml(String(data.body ?? ""));
  }

  return {
    id,
    name: String(data.name ?? id),
    documentTitle: String(data.documentTitle ?? "Contrato de Prestação de Serviços"),
    bodyHtml,
    showImobiLogo: data.showImobiLogo !== false,
    showCompanyLogo: data.showCompanyLogo !== false,
    active: data.active !== false,
    createdAt: String(data.createdAt ?? ""),
    createdBy: String(data.createdBy ?? "system"),
    updatedAt: data.updatedAt ? String(data.updatedAt) : undefined,
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined
  };
}

export function subscribeContractTemplates(onChange: (templates: ContractTemplate[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "contractTemplates"), orderBy("name")),
    (snapshot) => onChange(snapshot.docs.map((templateDoc) => mapContractTemplate(templateDoc.id, templateDoc.data()))),
    (error) => onError(error.message)
  );
}

export async function saveContractTemplate(template: ContractTemplate, user: User): Promise<string> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  if (!template.name.trim()) {
    throw new Error("Informe o nome do modelo de contrato.");
  }

  const templateRef = template.id ? doc(db, "contractTemplates", template.id) : doc(collection(db, "contractTemplates"));
  await setDoc(templateRef, {
    name: template.name.trim(),
    documentTitle: template.documentTitle.trim() || "Contrato de Prestação de Serviços",
    bodyHtml: sanitizeContractHtml(template.bodyHtml),
    showImobiLogo: template.showImobiLogo,
    showCompanyLogo: template.showCompanyLogo,
    active: template.active,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    createdAt: template.createdAt || serverTimestamp(),
    createdBy: template.createdBy || user.uid
  }, { merge: true });

  return templateRef.id;
}

export async function setContractTemplateActive(templateId: string, active: boolean, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore não configurado.");
  }

  await setDoc(doc(db, "contractTemplates", templateId), {
    active,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  }, { merge: true });
}

export const contractMergeFieldHelp: Array<{ token: string; description: string }> = [
  { token: "{{empresa.razaoSocial}}", description: "Razão social da empresa" },
  { token: "{{empresa.nomeFantasia}}", description: "Nome fantasia da empresa" },
  { token: "{{empresa.cnpj}}", description: "CNPJ" },
  { token: "{{empresa.endereco}}", description: "Endereço completo (logradouro, cidade, UF, CEP)" },
  { token: "{{empresa.telefone}}", description: "Telefone" },
  { token: "{{empresa.email}}", description: "E-mail" },
  { token: "{{empresa.plano}}", description: "Nome do plano contratado" },
  { token: "{{empresa.limiteBens}}", description: "Limite de bens do plano" },
  { token: "{{empresa.limiteUsuarios}}", description: "Limite de usuários do plano" },
  { token: "{{empresa.valorBase}}", description: "Valor mensal do plano, sem ajuste" },
  { token: "{{empresa.valorParcela}}", description: "Valor da parcela mensal, já com ajuste aplicado" },
  { token: "{{empresa.ajuste}}", description: "Descrição do desconto/acréscimo aplicado ao plano" },
  { token: "{{empresa.dataTesteAte}}", description: "Data final do período de teste" },
  { token: "{{empresa.dataExpiracaoPlano}}", description: "Data de expiração do plano/período contratado" },
  { token: "{{empresa.responsavelNome}}", description: "Nome do responsável cadastrado pela empresa" },
  { token: "{{empresa.responsavelContato}}", description: "Contato do responsável cadastrado pela empresa" },
  { token: "{{data.hoje}}", description: "Data de geração do contrato" },
  { token: "{{usuario.nome}}", description: "Usuário que gerou o contrato" },
  { token: "{{representante.nome}}", description: "Nome do representante da contratante" },
  { token: "{{representante.cpf}}", description: "CPF do representante da contratante" }
];

export function buildContractMergeFields(
  company: CompanyRecord,
  generatedBy: string,
  representativeName = "",
  representativeCpf = "",
  serviceCatalog: ServiceCatalogItem[] = []
): Record<string, string> {
  const addressParts = [company.address, company.city && company.state ? `${company.city}/${company.state}` : company.city || company.state, company.zipCode].filter(Boolean);
  const pricing = computeCompanyPlanPricing(company, serviceCatalog);
  const adjustmentType = company.settings?.commercialAdjustmentType;
  const adjustmentValue = company.settings?.commercialAdjustmentValue ?? 0;
  const adjustmentLabel = adjustmentType === "discount"
    ? `Desconto de ${formatCurrencyBR(adjustmentValue)}`
    : adjustmentType === "increase"
      ? `Acréscimo de ${formatCurrencyBR(adjustmentValue)}`
      : "Sem ajuste";

  return {
    "empresa.razaoSocial": company.legalName || "-",
    "empresa.nomeFantasia": company.tradeName || "-",
    "empresa.cnpj": company.cnpj || "-",
    "empresa.endereco": addressParts.length ? addressParts.join(", ") : "-",
    "empresa.telefone": company.phone || "-",
    "empresa.email": company.email || "-",
    "empresa.plano": company.settings?.activePlanName || "-",
    "empresa.limiteBens": company.settings?.maxAssets ? String(company.settings.maxAssets) : "sem limite",
    "empresa.limiteUsuarios": company.settings?.maxUsers ? String(company.settings.maxUsers) : "sem limite",
    "empresa.valorBase": pricing.baseValue !== undefined ? formatCurrencyBR(pricing.baseValue) : "-",
    "empresa.valorParcela": pricing.finalValue !== undefined ? formatCurrencyBR(pricing.finalValue) : "-",
    "empresa.ajuste": adjustmentLabel,
    "empresa.dataTesteAte": formatDateBR(company.settings?.trialUntil),
    "empresa.dataExpiracaoPlano": formatDateBR(company.settings?.planExpiresAt),
    "empresa.responsavelNome": company.responsibleName || "-",
    "empresa.responsavelContato": company.responsibleContact || "-",
    "data.hoje": new Date().toLocaleDateString("pt-BR"),
    "usuario.nome": generatedBy,
    "representante.nome": representativeName || "-",
    "representante.cpf": representativeCpf || "-"
  };
}

export function applyContractMergeFields(text: string, fields: Record<string, string>): string {
  return text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (match, token: string) => fields[token] ?? match);
}

const PAGE_WIDTH_MM = 210;
const MARGIN_LEFT_MM = 20;
const MARGIN_RIGHT_MM = 20;
const CONTENT_WIDTH_MM = PAGE_WIDTH_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
const CONTENT_TOP_MM = 40;
const CONTENT_BOTTOM_MM = 280;
const LINE_HEIGHT_MM = 5.2;
const BULLET_INDENT_MM = 6;
const BODY_FONT_SIZE = 10.5;
const INK_COLOR: [number, number, number] = [15, 23, 42];

function addContractFooter(doc: jsPDF, version: string, generatedAt: string, generatedBy: string) {
  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    doc.setDrawColor(226, 232, 240);
    doc.line(MARGIN_LEFT_MM, 288, PAGE_WIDTH_MM - MARGIN_RIGHT_MM, 288);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text(`IMOBI v${version} | Gerado em ${generatedAt} | Usuário: ${generatedBy}`, MARGIN_LEFT_MM, 293);
    doc.text(`Página ${page} de ${totalPages}`, PAGE_WIDTH_MM - MARGIN_RIGHT_MM, 293, { align: "right" });
  }
  doc.setTextColor(0, 0, 0);
}

function parseTextColor(value: string | undefined | null): [number, number, number] {
  if (!value) {
    return INK_COLOR;
  }

  const rgbMatch = value.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbMatch) {
    return [Number(rgbMatch[1]), Number(rgbMatch[2]), Number(rgbMatch[3])];
  }

  const normalized = value.replace("#", "");
  const hex = normalized.length === 3 ? normalized.split("").map((char) => char + char).join("") : normalized;
  const parsed = Number.parseInt(hex, 16);
  if (Number.isNaN(parsed) || hex.length !== 6) {
    return INK_COLOR;
  }
  return [(parsed >> 16) & 255, (parsed >> 8) & 255, parsed & 255];
}

type ContractRun = { text: string; bold: boolean; underline: boolean; color?: string; fontSize?: number };
type ContractParagraph = { bullet: boolean; runs: ContractRun[] };
type ContractRunState = { bold: boolean; underline: boolean; color?: string; fontSize?: number };

function splitNodesAtBreaks(nodes: ChildNode[]): ChildNode[][] {
  const groups: ChildNode[][] = [[]];
  nodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === "BR") {
      groups.push([]);
    } else {
      groups[groups.length - 1].push(node);
    }
  });
  return groups;
}

function parseFontSizePt(value: string | undefined): number | undefined {
  const match = value?.match(/([\d.]+)pt/);
  return match ? Number(match[1]) : undefined;
}

function collectContractRuns(nodes: ChildNode[], state: ContractRunState): ContractRun[] {
  const runs: ContractRun[] = [];
  nodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? "";
      if (text) {
        runs.push({ text, ...state });
      }
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }
    const element = node as HTMLElement;
    const tag = element.tagName;
    if (tag === "BR") {
      return;
    }
    // execCommand's output shape (<b> vs. inline font-weight, <font color> vs.
    // inline color) varies by browser/mode, so both are checked here.
    const fontWeight = element.style?.fontWeight;
    const styledBold = fontWeight === "bold" || (fontWeight ? Number(fontWeight) >= 600 : false);
    const nextState: ContractRunState = {
      bold: state.bold || tag === "B" || tag === "STRONG" || styledBold,
      underline: state.underline || tag === "U" || Boolean(element.style?.textDecoration?.includes("underline")),
      color: element.style?.color || (tag === "FONT" ? element.getAttribute("color") ?? undefined : undefined) || state.color,
      fontSize: parseFontSizePt(element.style?.fontSize) ?? state.fontSize
    };
    runs.push(...collectContractRuns(Array.from(element.childNodes), nextState));
  });
  return runs;
}

/** Parses the rich-text editor's HTML into paragraphs of styled runs for PDF layout. */
function parseContractHtml(html: string): ContractParagraph[] {
  const paragraphs: ContractParagraph[] = [];
  if (typeof window === "undefined") {
    return paragraphs;
  }

  const parsed = new DOMParser().parseFromString(html || "", "text/html");
  let pendingInline: ChildNode[] = [];

  function pushParagraph(nodes: ChildNode[], bullet: boolean) {
    splitNodesAtBreaks(nodes).forEach((group) => {
      const runs = collectContractRuns(group, { bold: false, underline: false });
      if (runs.some((run) => run.text.trim())) {
        paragraphs.push({ bullet, runs });
      }
    });
  }

  function flushPendingInline() {
    if (pendingInline.length) {
      pushParagraph(pendingInline, false);
      pendingInline = [];
    }
  }

  Array.from(parsed.body.childNodes).forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      const tag = element.tagName;
      if (tag === "UL" || tag === "OL") {
        flushPendingInline();
        Array.from(element.children).forEach((item) => pushParagraph(Array.from(item.childNodes), true));
        return;
      }
      if (tag === "P" || tag === "DIV") {
        flushPendingInline();
        pushParagraph(Array.from(element.childNodes), false);
        return;
      }
      pendingInline.push(node);
      return;
    }
    if (node.nodeType === Node.TEXT_NODE) {
      pendingInline.push(node);
    }
  });
  flushPendingInline();

  return paragraphs;
}

export function buildContractFileName(documentTitle: string, cnpjRaw: string, legalName: string): string {
  const parts = [documentTitle.trim(), cnpjRaw.replace(/\D/g, ""), legalName.trim()].filter(Boolean);
  const raw = `${parts.join(" - ")}.pdf`;
  return raw.replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, " ").trim();
}

async function buildContractPdf({
  company,
  template,
  generatedBy,
  representativeName,
  representativeCpf,
  serviceCatalog = [],
  imobiLogoUrl,
  version = "0.1.70"
}: {
  company: CompanyRecord;
  template: ContractTemplate;
  generatedBy: string;
  representativeName: string;
  representativeCpf?: string;
  serviceCatalog?: ServiceCatalogItem[];
  imobiLogoUrl?: string;
  version?: string;
}): Promise<{ doc: jsPDF; generatedAt: string; fileName: string }> {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const generatedAt = new Date().toLocaleString("pt-BR");
  const mergeFields = buildContractMergeFields(company, generatedBy, representativeName, representativeCpf, serviceCatalog);
  const resolvedTitle = applyContractMergeFields(template.documentTitle, mergeFields);
  const resolvedParagraphs: ContractParagraph[] = parseContractHtml(template.bodyHtml).map((paragraph) => ({
    bullet: paragraph.bullet,
    runs: paragraph.runs.map((run) => ({ ...run, text: applyContractMergeFields(run.text, mergeFields) }))
  }));

  const imobiLogo = template.showImobiLogo ? await loadPdfImage(imobiLogoUrl) : null;
  const companyLogo = template.showCompanyLogo ? await loadPdfImage(company.logoUrl) : null;

  function renderHeader() {
    if (imobiLogo) {
      doc.addImage(imobiLogo.dataUrl, imobiLogo.format, MARGIN_LEFT_MM, 10, 24, 16, undefined, "FAST");
    }
    if (companyLogo) {
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(PAGE_WIDTH_MM - MARGIN_RIGHT_MM - 21, 9, 21, 17, 2, 2);
      doc.addImage(companyLogo.dataUrl, companyLogo.format, PAGE_WIDTH_MM - MARGIN_RIGHT_MM - 19, 11, 17, 13, undefined, "FAST");
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(...INK_COLOR);
    doc.text(resolvedTitle, PAGE_WIDTH_MM / 2, 20, { align: "center", maxWidth: CONTENT_WIDTH_MM - 50 });
    doc.setDrawColor(226, 232, 240);
    doc.line(MARGIN_LEFT_MM, 32, PAGE_WIDTH_MM - MARGIN_RIGHT_MM, 32);
  }

  function setBodyStyle(bold: boolean, color: [number, number, number], fontSize: number) {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(fontSize);
    doc.setTextColor(...color);
  }

  renderHeader();

  let cursorY = CONTENT_TOP_MM;

  type Word = { text: string; bold: boolean; underline: boolean; color: [number, number, number]; fontSize: number };

  function renderParagraph(paragraph: ContractParagraph) {
    const words: Word[] = [];
    paragraph.runs.forEach((run) => {
      run.text.split(/\s+/).filter(Boolean).forEach((text) => {
        words.push({
          text,
          bold: run.bold,
          underline: run.underline,
          color: parseTextColor(run.color),
          fontSize: run.fontSize ?? BODY_FONT_SIZE
        });
      });
    });
    if (!words.length) {
      return;
    }

    const indent = paragraph.bullet ? BULLET_INDENT_MM : 0;
    const startX = MARGIN_LEFT_MM + indent;
    const maxWidth = CONTENT_WIDTH_MM - indent;

    let lineWords: Word[] = [];
    let lineWidth = 0;
    let isFirstLine = true;

    function flushLine() {
      if (!lineWords.length) {
        return;
      }
      const lineFontSize = Math.max(...lineWords.map((word) => word.fontSize));
      const lineHeight = Math.max(LINE_HEIGHT_MM, (lineFontSize / BODY_FONT_SIZE) * LINE_HEIGHT_MM);
      if (cursorY > CONTENT_BOTTOM_MM) {
        doc.addPage();
        renderHeader();
        cursorY = CONTENT_TOP_MM;
      }
      if (paragraph.bullet && isFirstLine) {
        setBodyStyle(false, INK_COLOR, BODY_FONT_SIZE);
        doc.text("•", MARGIN_LEFT_MM, cursorY);
      }
      let x = startX;
      lineWords.forEach((word) => {
        setBodyStyle(word.bold, word.color, word.fontSize);
        doc.text(word.text, x, cursorY);
        const wordWidth = doc.getTextWidth(word.text);
        if (word.underline) {
          doc.setDrawColor(...word.color);
          doc.setLineWidth(0.15);
          doc.line(x, cursorY + 0.8, x + wordWidth, cursorY + 0.8);
        }
        x += doc.getTextWidth(`${word.text} `);
      });
      cursorY += lineHeight;
      lineWords = [];
      lineWidth = 0;
      isFirstLine = false;
    }

    words.forEach((word) => {
      doc.setFont("helvetica", word.bold ? "bold" : "normal");
      doc.setFontSize(word.fontSize);
      const spaceWidth = lineWords.length ? doc.getTextWidth(" ") : 0;
      const wordWidth = doc.getTextWidth(word.text);
      if (lineWords.length && lineWidth + spaceWidth + wordWidth > maxWidth) {
        flushLine();
      }
      lineWords.push(word);
      lineWidth += (lineWords.length > 1 ? spaceWidth : 0) + wordWidth;
    });
    flushLine();

    cursorY += LINE_HEIGHT_MM * 0.6;
  }

  resolvedParagraphs.forEach(renderParagraph);

  if (cursorY > CONTENT_BOTTOM_MM - 40) {
    doc.addPage();
    renderHeader();
    cursorY = CONTENT_TOP_MM;
  }

  cursorY += LINE_HEIGHT_MM * 2;
  doc.setDrawColor(148, 163, 184);
  doc.line(MARGIN_LEFT_MM, cursorY, MARGIN_LEFT_MM + 70, cursorY);
  doc.line(PAGE_WIDTH_MM - MARGIN_RIGHT_MM - 70, cursorY, PAGE_WIDTH_MM - MARGIN_RIGHT_MM, cursorY);
  cursorY += LINE_HEIGHT_MM;
  setBodyStyle(false, INK_COLOR, 9);
  doc.text("IMOBI", MARGIN_LEFT_MM, cursorY);
  doc.text(company.tradeName || company.legalName || "Empresa contratante", PAGE_WIDTH_MM - MARGIN_RIGHT_MM - 70, cursorY);
  cursorY += LINE_HEIGHT_MM;
  doc.setFontSize(8);
  doc.setTextColor(71, 85, 105);
  doc.text(`${representativeName}${representativeCpf ? ` - CPF: ${representativeCpf}` : ""}`, PAGE_WIDTH_MM - MARGIN_RIGHT_MM - 70, cursorY);

  addContractFooter(doc, version, generatedAt, generatedBy);

  return {
    doc,
    generatedAt,
    fileName: buildContractFileName(resolvedTitle, company.cnpjRaw || company.cnpj, company.legalName)
  };
}

export type GenerateContractInput = {
  company: CompanyRecord;
  template: ContractTemplate;
  generatedBy: string;
  representativeName: string;
  representativeCpf?: string;
  serviceCatalog?: ServiceCatalogItem[];
  imobiLogoUrl?: string;
  version?: string;
};

export async function generateAndSaveContract(input: GenerateContractInput, user: User): Promise<GeneratedContract> {
  if (!db || !storage) {
    throw new Error("Firestore ou Storage não configurados.");
  }

  if (!input.representativeName.trim()) {
    throw new Error("Informe o nome do representante que vai assinar pela contratante.");
  }

  const { doc: pdfDoc, fileName } = await buildContractPdf(input);
  const pdfBlob = pdfDoc.output("blob");

  const contractRef = doc(collection(db, "generatedContracts"));
  const fileRef = ref(storage, `contracts/${contractRef.id}/generated.pdf`);
  await uploadBytes(fileRef, pdfBlob, { contentType: "application/pdf" });
  const pdfUrl = await getDownloadURL(fileRef);

  const record: Omit<GeneratedContract, "id"> = {
    companyId: input.company.id,
    companyName: input.company.tradeName || input.company.legalName,
    companyCnpj: input.company.cnpj,
    templateId: input.template.id,
    templateName: input.template.name,
    documentTitle: input.template.documentTitle,
    representativeName: input.representativeName.trim(),
    representativeCpf: input.representativeCpf?.trim() || undefined,
    fileName,
    pdfUrl,
    generatedByName: input.generatedBy,
    status: "gerado",
    createdAt: "",
    createdBy: user.uid
  };

  await setDoc(contractRef, {
    ...record,
    createdAt: serverTimestamp()
  });

  pdfDoc.save(fileName);

  return { ...record, id: contractRef.id, createdAt: new Date().toISOString() };
}

function serializeContractTimestamp(value: unknown): string {
  if (value && typeof value === "object" && "toDate" in value) {
    return (value as { toDate: () => Date }).toDate().toISOString();
  }
  return typeof value === "string" ? value : "";
}

function mapGeneratedContract(id: string, data: Record<string, unknown>): GeneratedContract {
  return {
    id,
    companyId: String(data.companyId ?? ""),
    companyName: String(data.companyName ?? ""),
    companyCnpj: String(data.companyCnpj ?? ""),
    templateId: String(data.templateId ?? ""),
    templateName: String(data.templateName ?? ""),
    documentTitle: String(data.documentTitle ?? ""),
    representativeName: String(data.representativeName ?? ""),
    representativeCpf: data.representativeCpf ? String(data.representativeCpf) : undefined,
    fileName: String(data.fileName ?? ""),
    pdfUrl: String(data.pdfUrl ?? ""),
    generatedByName: String(data.generatedByName ?? ""),
    status: (data.status === "assinado" ? "assinado" : "gerado") as GeneratedContractStatus,
    signedAt: data.signedAt ? serializeContractTimestamp(data.signedAt) : undefined,
    signedByUserId: data.signedByUserId ? String(data.signedByUserId) : undefined,
    signedByUserName: data.signedByUserName ? String(data.signedByUserName) : undefined,
    signedFileUrl: data.signedFileUrl ? String(data.signedFileUrl) : undefined,
    createdAt: serializeContractTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? ""),
    updatedAt: data.updatedAt ? serializeContractTimestamp(data.updatedAt) : undefined,
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined
  };
}

export function subscribeGeneratedContracts(onChange: (contracts: GeneratedContract[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "generatedContracts"), orderBy("createdAt", "desc")),
    (snapshot) => onChange(snapshot.docs.map((contractDoc) => mapGeneratedContract(contractDoc.id, contractDoc.data()))),
    (error) => onError(error.message)
  );
}

export async function uploadSignedContract(contractId: string, file: File, user: User): Promise<void> {
  if (!db || !storage) {
    throw new Error("Firestore ou Storage não configurados.");
  }

  const extension = file.name.split(".").pop()?.toLowerCase() || "pdf";
  const fileRef = ref(storage, `contracts/${contractId}/signed.${extension}`);
  await uploadBytes(fileRef, file, { contentType: file.type || "application/pdf" });
  const signedFileUrl = await getDownloadURL(fileRef);

  await updateDoc(doc(db, "generatedContracts", contractId), {
    status: "assinado",
    signedAt: serverTimestamp(),
    signedByUserId: user.uid,
    signedByUserName: user.displayName ?? user.email ?? user.uid,
    signedFileUrl,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}
